import { useEffect, useReducer, useRef } from 'react';
import ClientSettingsService from '../../api/clientSettingsService';
import { ClientSettings } from '../../types/clientSettings';
import toast from 'react-hot-toast';

type Action =
  | { type: 'fetch' }
  | { type: 'loaded'; value: ClientSettings[] | null }
  | { type: 'update'; clientSettings: ClientSettings }
  | { type: 'delete'; id: number }
  | { type: 'add' };

interface ClientSettingsState {
  Records: ClientSettings[];
  Loading: boolean;
}

const DefaultClientSettingsState = {
  Records: [],
  Loading: false,
} as ClientSettingsState;

const reducer = (
  state: ClientSettingsState,
  action: Action
): ClientSettingsState => {
  switch (action.type) {
    case 'fetch':
      return { ...state, Loading: true };
    case 'loaded': {
      if (action.value === null) {
        return { ...state, Loading: false };
      }
      return { ...state, Records: [...action.value], Loading: false };
    }
    case 'update': {
      const recordsWithoutTemp = state.Records.filter((r) => r.id !== 0);
      const index = recordsWithoutTemp.findIndex(
        (r) => r.id === action.clientSettings.id
      );
      if (index === -1) {
        return { ...state, Records: [...recordsWithoutTemp, action.clientSettings] };
      }
      return {
        ...state,
        Records: recordsWithoutTemp.map((r, i) =>
          i === index ? action.clientSettings : r
        ),
      };
    }
    case 'delete': {
      return {
        ...state,
        Records: state.Records.filter((r) => r.id !== action.id),
      };
    }
    case 'add': {
      const systemRecord = state.Records.find((r) => r.mask === null);
      const newRecord: ClientSettings = {
        id: 0,
        mask: '',
        metadata: systemRecord
          ? {
              devBuild: systemRecord.metadata.devBuild,
              firstBoosterTutorialLevel: systemRecord.metadata.firstBoosterTutorialLevel,
              firstPurchaseTutorialLevel: systemRecord.metadata.firstPurchaseTutorialLevel,
              challengeTutorialLevel: systemRecord.metadata.challengeTutorialLevel,
              noAds: systemRecord.metadata.noAds,
              adsFirstLevel: systemRecord.metadata.adsFirstLevel,
              interstitialEveryLevel: systemRecord.metadata.interstitialEveryLevel,
              noPurchase: systemRecord.metadata.noPurchase,
              dailyBonusEnabled: systemRecord.metadata.dailyBonusEnabled,
              dailyBonusRewardAmount: systemRecord.metadata.dailyBonusRewardAmount,
              wordDescriptionEnabled: systemRecord.metadata.wordDescriptionEnabled,
              missedWordReportEnabled: systemRecord.metadata.missedWordReportEnabled,
            }
          : {
              devBuild: false,
              firstBoosterTutorialLevel: 0,
              firstPurchaseTutorialLevel: 0,
              challengeTutorialLevel: 0,
              noAds: false,
              adsFirstLevel: 0,
              interstitialEveryLevel: 0,
              noPurchase: false,
              dailyBonusEnabled: true,
              dailyBonusRewardAmount: 0,
              wordDescriptionEnabled: true,
              missedWordReportEnabled: true,
            },
      };
      return {
        ...state,
        Records: [...state.Records, newRecord],
      };
    }
    default:
      return state;
  }
};

export const useClientSettingsReducer = () => {
  const [data, dispatch] = useReducer(reducer, DefaultClientSettingsState);
  const loading = useRef<boolean>(false);

  useEffect(() => {
    if (loading.current) return;
    loading.current = true;
    fetchData();
  }, []);

  const fetchData = async () => {
    dispatch({ type: 'fetch' });
    try {
      const records = await ClientSettingsService.getAll();
      dispatch({ type: 'loaded', value: records });
    } catch (error) {
      dispatch({ type: 'loaded', value: null });
      toast.error('Failed to load client settings');
    } finally {
      loading.current = false;
    }
  };

  const addRecord = () => {
    if (!data.Records.some((r) => r.id === 0)) {
      dispatch({ type: 'add' });
    }
  };

  const update = async (
    clientSettings: ClientSettings
  ): Promise<boolean> => {
    try {
      if (clientSettings.id === 0) {
        if (!clientSettings.mask || clientSettings.mask.trim() === '') {
          toast.error('Mask is required');
          return false;
        }
        const created = await ClientSettingsService.create(clientSettings);
        dispatch({ type: 'update', clientSettings: created });
        toast.success('Client settings created');
        return true;
      } else {
        const updated = await ClientSettingsService.update(clientSettings);
        dispatch({ type: 'update', clientSettings: updated });
        toast.success('Client settings updated');
        return true;
      }
    } catch (error) {
      toast.error('Failed to save client settings');
      return false;
    }
  };

  const deleteRecord = async (id: number): Promise<boolean> => {
    if (id === 0) {
      dispatch({ type: 'delete', id });
      return true;
    }
    try {
      const success = await ClientSettingsService.delete(id);
      if (success) {
        dispatch({ type: 'delete', id });
        toast.success('Client settings deleted');
        return true;
      } else {
        toast.error('Failed to delete client settings');
        return false;
      }
    } catch (error) {
      toast.error('Failed to delete client settings');
      return false;
    }
  };

  return {
    data,
    addRecord,
    update,
    deleteRecord,
  };
};
